import { Switch, Tooltip } from '@mui/material'
import { toast } from 'react-toastify'

import { useChangeBookingSchedulerControlStatusMutation, useGetAppFeatureByNameQuery } from 'generated/graphql'
import { GET_BOOKING_SCHEDULER_CONTROLS } from 'graphql/queries/BookingSchedulerControlQueries'
import { GetWecAiAppFeatureNameFromSchedulerType } from 'helpers'
import { usePermission } from 'providers'

interface IBookingSchedulerControlSwitchProps {
  schedulerType: string
  state: boolean
  bookingId: string | null
  noPermissionHelperText?: string
  featureDisabledHelperText?: string
}

const BookingSchedulerControlSwitch = ({
  schedulerType,
  state,
  bookingId,
  noPermissionHelperText = "You don't have permission to change this setting.",
  featureDisabledHelperText = 'This feature is currently disabled.',
}: IBookingSchedulerControlSwitchProps) => {
  const [isAllowedToSwitchBookingSchedulerControl] = usePermission('UpdateBookingSchedulerControls')

  const { data: featureData } = useGetAppFeatureByNameQuery({
    variables: { name: GetWecAiAppFeatureNameFromSchedulerType(schedulerType) },
    fetchPolicy: 'network-only',
  })

  const [changeBookingSchedulerControlStatus, { loading }] =
    useChangeBookingSchedulerControlStatusMutation({
      refetchQueries: [{ query: GET_BOOKING_SCHEDULER_CONTROLS, variables: { id: Number(bookingId) } }],
      awaitRefetchQueries: true,
    })

  const handleChange = async () => {
    if (!isAllowedToSwitchBookingSchedulerControl) return

    try {
      await changeBookingSchedulerControlStatus({
        variables: { schedulerType: schedulerType, bookingId: Number(bookingId) },
      })
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Something went wrong.')
      console.error(e)
    }
  }

  const isFeatureEnabled = !!featureData?.appFeatureByName?.appFeatureStatus

  const isDisabled = () => {
    if (!isAllowedToSwitchBookingSchedulerControl || loading) return true

    if (!isFeatureEnabled) return true

    return false
  }

  const getDisabledHelperText = () => {
    if (!isAllowedToSwitchBookingSchedulerControl) return noPermissionHelperText

    if (!isFeatureEnabled) return featureDisabledHelperText

    return ''
  }

  return (
    <Tooltip title={isDisabled() ? getDisabledHelperText() : ''} arrow>
      <span>
        <Switch
          checked={state}
          onChange={handleChange}
          disabled={isDisabled()}
        />
      </span>
    </Tooltip>
  )
}

export default BookingSchedulerControlSwitch

